import { useV2BannersControllerFetchBanners, type V2BannerListResponseDto, V2BannersControllerFetchBannersCategory as BannerCategory } from "@everon-it/api-client/contents-gw";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import Table from "@components/atoms/Table";
import Filter from "@components/molecules/Filter";
import { PrivateRoutes } from "@constants/urls";
import useGlobalModal from "@hooks/useGlobalModal";
import { usePagination } from "@hooks/usePagination";
import useRouter from "@hooks/useRouter";
import { colors, typo } from "@styles/index";
import { DateUtil } from "@everon-it/core";
import { Pagination } from "antd";
import { useFormik } from "formik";
import React from "react";
import styled from "styled-components";
import { bannerData, type BannerInitialValues } from "./Data";

type BannerTableListDataType = {
    displayDateAt: string;
    rowNumber: number;
} & Omit<V2BannerListResponseDto, "displayStartAt" | "displayEndAt">;

const BannerManagement: React.FC = () => {
    // Data
    const filter = bannerData.forms;
    const formInitialValues = bannerData.formInitialValues;
    const tableContentsInfoData = bannerData.tableList;

    // Props
    const MAIN_TITLE = "팝업/배너 관리";
    const TABLE_TITLE = "팝업/배너 목록";
    const FILTER_GIRD_COLUMN = 2;
    const tableButtonText = "배너 등록";

    // Hook Props
    const DETAIL_PATH = PrivateRoutes.BannerDetail;
    const CREATE_PATH = PrivateRoutes.BannerCreate;

    const { push } = useRouter();
    const { showOrderManagementModal } = useGlobalModal();
    const { page, pageSize, setPage, handlePageSizeChange, resetPage, getRowNumber, setSearchParams, searchParams } = usePagination({ syncWithUrl: true });

    // URL 파라미터에서 초기값 읽기
    const getInitialValues = (): BannerInitialValues => ({
        title: searchParams.get("title") ?? formInitialValues.title,
        category: (searchParams.get("category") as BannerCategory | "" | null) ?? formInitialValues.category,
        isVisible: searchParams.get("isVisible") ?? formInitialValues.isVisible,
        startDate: searchParams.get("startDate") ?? formInitialValues.startDate,
        endDate: searchParams.get("endDate") ?? formInitialValues.endDate,
    });

    // Formik
    const formik = useFormik<BannerInitialValues>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: values => {
            const filteredData: Record<string, string> = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value !== "") {
                    filteredData[key] = String(value);
                }
            });
            setSearchParams(filteredData);
            resetPage();
        },
    });

    // 검색 필터 초기화
    const handleFormReset = () => {
        setSearchParams({});
        resetPage();
        formik.resetForm();
    };

    // api-client: 배너 목록 조회
    const { data } = useV2BannersControllerFetchBanners(
        {
            title: formik.values.title || undefined,
            category: formik.values.category ? formik.values.category : undefined,
            isVisible: formik.values.isVisible ? formik.values.isVisible === "true" : undefined,
            startDate: formik.values.startDate || undefined,
            endDate: formik.values.endDate || undefined,
            page,
            pageSize,
        },
        {
            query: {
                retry: false,
                refetchOnWindowFocus: false,
            },
        },
    );

    const listData: V2BannerListResponseDto[] = data?.rows ?? [];
    const totalCount = data?.count ?? 0;

    /** 작성 페이지 이동 */
    const handleCreateClick = () => {
        push(CREATE_PATH);
    };

    /** 상세 페이지 이동 id */
    const handleDetailClick = (uuid: string) => {
        push(`${DETAIL_PATH}/${uuid}`);
    };

    const bannerTableListData: BannerTableListDataType[] = listData.map((item, index) => {
        const { displayStartAt, displayEndAt, ...rest } = item;
        return {
            ...rest,
            displayDateAt: `${DateUtil.toDateTimeString(displayStartAt)} ~ ${DateUtil.toDateTimeString(displayEndAt)}`,
            rowNumber: getRowNumber(index),
        };
    });

    return (
        <ContentsWrap>
            <MainHeaderTitle>{MAIN_TITLE}</MainHeaderTitle>

            <ContentBox>
                <Filter formik={formik} filterData={filter} girdColumn={FILTER_GIRD_COLUMN} formInitialValues={formInitialValues} handleFormReset={handleFormReset} />
            </ContentBox>

            <ContentBoxWithHeader>
                <TableHeader>
                    <TableTitle>{TABLE_TITLE}</TableTitle>
                    <HeaderButtonWrap>
                        <Button
                            buttonSize="small"
                            buttonType="line_black"
                            text="팝업 노출 순번 관리"
                            onClick={() => {
                                if (listData.length > 0) {
                                    showOrderManagementModal({
                                        modalHeaderTitle: "팝업 노출 순번 관리",
                                        contentTitle: "팝업 노출 목록",
                                        firstButtonText: "닫기",
                                        secondButtonText: "저장",
                                        category: BannerCategory.POPUP,
                                    });
                                }
                            }}
                        />
                        <Button
                            buttonSize="small"
                            buttonType="line_black"
                            text="홈 띠 배너 순번관리"
                            onClick={() => {
                                if (listData.length > 0) {
                                    showOrderManagementModal({
                                        modalHeaderTitle: "홈 띠 배너 순번 관리",
                                        contentTitle: "홈 띠 배너 노출 목록",
                                        firstButtonText: "닫기",
                                        secondButtonText: "저장",
                                        category: BannerCategory.BANNER,
                                    });
                                }
                            }}
                        />
                        <Button buttonSize="small" text={tableButtonText} onClick={handleCreateClick} />
                    </HeaderButtonWrap>
                </TableHeader>
                <Table<BannerTableListDataType> rowData={bannerTableListData} tableContentsInfoData={tableContentsInfoData} currentPageSize={pageSize} onDetailClick={handleDetailClick} />
                <TableBottomWrap>
                    <ContentsTotal>
                        총<span>{totalCount}</span>
                    </ContentsTotal>
                    <PageNationWrap>
                        <Pagination
                            defaultCurrent={page}
                            defaultPageSize={pageSize}
                            total={totalCount}
                            showSizeChanger
                            locale={{ items_per_page: "개씩 보기" }}
                            current={page}
                            pageSize={pageSize}
                            onShowSizeChange={handlePageSizeChange}
                            onChange={setPage}
                        />
                    </PageNationWrap>
                </TableBottomWrap>
            </ContentBoxWithHeader>
        </ContentsWrap>
    );
};

export default BannerManagement;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

/** 두번째 컨테이너 스타일 */
const ContentBoxWithHeader = styled.div`
    margin-top: 20px;
    padding: 22px 20px;
    background: ${colors.WHITE};
    border-radius: 16px;
    border: 1px solid ${colors.GRAY9};
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
`;

const HeaderButtonWrap = styled.div`
    display: flex;
    max-height: 32px;
    gap: 8px;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;

const PageNationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
